import { useCallback, useEffect, useRef, useState } from 'react';
import { PasswordInput } from '@/components/PasswordInput';
import { useTestGitConnectionMutation, useTestGitConnectionDraftMutation } from '@/services/api';
import { parseApiError } from '@/utils/apiError';
import type { GitAuthMode, GitConnectionOutcome, GitProtocol } from '@/types/api';
import {
  browseUrlToCloneUrl,
  credentialPlaceholder,
  detectGitProvider,
  type GitProviderKind
} from './gitProvider';


const OUTCOME_MESSAGE: Record<GitConnectionOutcome, string> = {
  success: 'Connected — ls-remote OK.',
  auth_failed: 'Authentication failed — check the credential and try again.',
  not_found: 'Repository not found — check the URL.',
  network_error: 'Network error reaching the host — try again.',
  unsupported_protocol: 'Unsupported protocol — use an SSH or HTTPS Git URL.'
};

function authFailedMessage(kind: GitProviderKind, mode: GitAuthMode): string {
  if (kind !== 'bitbucket_server') return OUTCOME_MESSAGE.auth_failed;
  return mode === 'bearer'
    ? 'Authentication failed. This mode sends no username, which is correct for a project or repository token. If the token was created under your own profile instead, switch to "Personal token" and enter your username.'
    : 'Authentication failed. Check the username and token. If this token was created on the project or repository rather than under your own profile, switch to "Project / repository token" — that type cannot be used with a username at all.';
}

export interface GitConnectionValue {
  git_url: string;
  protocol: GitProtocol;
  credential_id: string;
  credential_plaintext?: string;
  git_username?: string;
  git_auth_mode?: GitAuthMode;
  default_branch: string;
  verified: boolean;
}

export function branchFieldFor(raw: string | undefined): { default_branch: string } {
  return { default_branch: (raw ?? '').trim() };
}

export function GitConnectionHelper({
  projectId,
  initial,
  onConnected,
  showCredentialField = true,
  testLabel,
  hideAction = false,
  onAction,
  externalCredential
}: {
  projectId: string;
  initial?: Partial<GitConnectionValue>;
  onConnected: (value: GitConnectionValue) => void;
  showCredentialField?: boolean;
  testLabel?: string;
  hideAction?: boolean;
  externalCredential?: string;
  onAction?: (action: { run: () => void; disabled: boolean; busy: boolean }) => void;
}): JSX.Element {
  const [testConnection, { isLoading: isTesting }] = useTestGitConnectionMutation();
  const [testConnectionDraft, { isLoading: isTestingDraft }] = useTestGitConnectionDraftMutation();
  const isLoading = isTesting || isTestingDraft;
  const [gitUrl, setGitUrl] = useState(initial?.git_url ?? '');
  const [gitUsername, setGitUsername] = useState(initial?.git_username ?? '');
  const [authMode, setAuthMode] = useState<GitAuthMode>(initial?.git_auth_mode ?? 'basic');
  const [defaultBranch, setDefaultBranch] = useState(initial?.default_branch ?? '');
  const [edited, setEdited] = useState(false);

  const seedKey = [
    initial?.git_url ?? '',
    initial?.git_username ?? '',
    initial?.default_branch ?? '',
    initial?.git_auth_mode ?? ''
  ].join('\u0000');
  const [lastSeed, setLastSeed] = useState(seedKey);
  if (!edited && seedKey !== lastSeed) {
    setLastSeed(seedKey);
    setGitUrl(initial?.git_url ?? '');
    setGitUsername(initial?.git_username ?? '');
    setDefaultBranch(initial?.default_branch ?? '');
    if (initial?.git_auth_mode !== undefined) setAuthMode(initial.git_auth_mode);
  }
  const [credential, setCredential] = useState('');
  const [result, setResult] = useState<{
    outcome: GitConnectionOutcome;
    head?: string;
    deferred?: boolean;
  } | null>(initial?.verified ? { outcome: 'success' } : null);
  const [error, setError] = useState<string | null>(null);

  const effectiveProtocol: GitProtocol = 'https';

  function branchField(next = defaultBranch): { default_branch: string } {
    return branchFieldFor(next);
  }

  function bubbleUnverified(
    nextUrl: string,
    nextCredential = credential,
    nextUsername = gitUsername,
    nextMode = authMode,
    nextBranch = defaultBranch,
    nextVerified = result?.outcome === 'success'
  ): void {
    if (nextUrl.trim() === '') return;
    onConnected({
      git_url: nextUrl.trim(),
      protocol: effectiveProtocol,
      credential_id: '',
      ...branchField(nextBranch),
      ...(nextCredential !== '' ? { credential_plaintext: nextCredential } : {}),
      git_username: nextMode === 'bearer' ? '' : nextUsername,
      git_auth_mode: nextMode,
      verified: nextVerified
    });
  }

  const isDraft = !/^[0-9a-f-]{36}$/i.test(projectId);

  async function onTest(): Promise<void> {
    setError(null);
    setResult(null);
    try {
      if (isDraft) {
        const res = await testConnectionDraft({
          git_url: gitUrl.trim(),
          protocol: effectiveProtocol,
          credential,
          git_username: authMode === 'bearer' ? '' : gitUsername.trim(),
          git_auth_mode: authMode
        }).unwrap();
        setResult({ outcome: res.outcome, head: res.head_sha });
        onConnected({
          git_url: gitUrl.trim(),
          protocol: effectiveProtocol,
          credential_id: '',
          ...branchField(),
          ...(credential !== '' ? { credential_plaintext: credential } : {}),
          git_username: authMode === 'bearer' ? '' : gitUsername.trim(),
          git_auth_mode: authMode,
          verified: res.outcome === 'success'
        });
        return;
      }
      const res = await testConnection({
        projectId,
        body: {
          git_url: gitUrl.trim(),
          protocol: effectiveProtocol,
          credential: credential !== '' ? credential : (externalCredential ?? ''),
          git_username: authMode === 'bearer' ? '' : gitUsername.trim(),
          git_auth_mode: authMode
        }
      }).unwrap();
      setResult({ outcome: res.outcome, head: res.head_sha });
      if (res.outcome === 'success') {
        onConnected({
          git_url: gitUrl.trim(),
          protocol: effectiveProtocol,
          credential_id: res.credential_id ?? '',
          ...branchField(),
          git_username: authMode === 'bearer' ? '' : gitUsername.trim(),
          git_auth_mode: authMode,
          verified: true
        });
        setCredential('');
      }
    } catch (err) {
      setError(parseApiError(err).message);
    }
  }

  const canTest = gitUrl.trim() !== '' && !isLoading;

  const onTestRef = useRef(onTest);
  onTestRef.current = onTest;
  const stableRun = useCallback(() => {
    void onTestRef.current();
  }, []);
  useEffect(() => {
    onAction?.({ run: stableRun, disabled: !canTest, busy: isLoading });
  }, [canTest, isLoading, stableRun]);

  const provider = detectGitProvider(gitUrl);
  const cloneSuggestion = browseUrlToCloneUrl(gitUrl);

  const usernameRequired =
    authMode === 'basic' &&
    (provider.kind === 'bitbucket_cloud' || provider.kind === 'bitbucket_server');

  function applySuggestion(nextUrl: string): void {
    setGitUrl(nextUrl);
    setResult(null);
    bubbleUnverified(nextUrl, credential, gitUsername, authMode, defaultBranch, false);
  }

  return (
    <div>
      <div className="field">
        <label className="field-label" htmlFor="git-url">
          Repository URL<span className="req">*</span>
        </label>
        <input
          id="git-url"
          className="input mono"
          autoComplete="off-git-url"
          placeholder="* https://host/scm/PROJECT/repo.git or https://github.com/org/repo.git"
          value={gitUrl}
          onChange={(e) => {
            setEdited(true);
            setResult(null);
            setGitUrl(e.target.value);
            bubbleUnverified(e.target.value, credential, gitUsername, authMode, defaultBranch, false);
          }}
        />
        {provider.kind !== 'unknown' && provider.label !== '' && (
          <p className="field-help">
            ✓ {provider.label} detected
            {provider.project_key !== undefined && (
              <>
                {' · project '}
                <code>{provider.project_key}</code>
                {' · repository '}
                <code>{provider.slug}</code>
              </>
            )}
          </p>
        )}
        {cloneSuggestion !== null && (
          <p className="field-help">
            That looks like a Bitbucket <strong>browse</strong> URL, not a clone URL. Use{' '}
            <code>{cloneSuggestion}</code> instead?{' '}
            <button
              type="button"
              className="btn sm"
              onClick={() => applySuggestion(cloneSuggestion)}
            >
              Use this
            </button>
          </p>
        )}
      </div>

      {(provider.kind === 'bitbucket_server' || provider.kind === 'bitbucket_cloud') && (
        <div className="field">
          <label className="field-label">Token type</label>
          <label className="radio-row">
            <input
              type="radio"
              name="git-auth-mode"
              checked={authMode === 'basic'}
              onChange={() => {
                setAuthMode('basic');
                setResult(null);
                bubbleUnverified(gitUrl, credential, gitUsername, 'basic', defaultBranch, false);
              }}
            />
            <span>
              <strong>Personal token</strong> — created under your own profile. Uses a username.
            </span>
          </label>
          <label className="radio-row">
            <input
              type="radio"
              name="git-auth-mode"
              checked={authMode === 'bearer'}
              onChange={() => {
                setAuthMode('bearer');
                setResult(null);
                bubbleUnverified(gitUrl, credential, gitUsername, 'bearer', defaultBranch, false);
              }}
            />
            <span>
              <strong>Project / repository token</strong> — created on the repository itself. No
              username.
            </span>
          </label>
          <p className="field-help">
            {authMode === 'basic' ? (
              <>
                Create it at: <strong>avatar → Manage account → HTTP access tokens</strong>. Needs
                Projects: Read and Repositories: Write.
              </>
            ) : (
              <>
                Create it at: <strong>Repository settings → HTTP access tokens</strong> (or Project
                settings, to cover every repository in the project). Needs Repository: Write.
              </>
            )}
          </p>
        </div>
      )}

      {(authMode === 'basic' || gitUsername !== '') && (
      <div className="field">
        <label className="field-label" htmlFor="git-username">
          Username{usernameRequired ? <span className="req">*</span> : null}
        </label>
        <input
          id="git-username"
          className="input mono"
          autoComplete="off-git-username"
          placeholder={usernameRequired ? '* your Bitbucket username' : 'your Bitbucket username'}
          value={gitUsername}
          onChange={(e) => {
            setEdited(true);
            setGitUsername(e.target.value);
            setResult(null);
            bubbleUnverified(gitUrl, credential, e.target.value, authMode, defaultBranch, false);
          }}
        />
        <p className="field-help">
          {authMode === 'bearer' ? (
            <>
              Not sent in this mode — a project or repository token authenticates without a
              username. Stored value shown so it can be cleared or corrected.
            </>
          ) : usernameRequired ? (
            <>A Bitbucket personal token authenticates as your username plus the token.</>
          ) : (
            <>
              Leave blank to use <code>x-token-auth</code>.
            </>
          )}
        </p>
      </div>
      )}

      <div className="field">
        <label className="field-label" htmlFor="git-default-branch">
          Default branch
        </label>
        <input
          id="git-default-branch"
          className="input mono"
          autoComplete="off-git-default-branch"
          placeholder="main"
          value={defaultBranch}
          onChange={(e) => {
            setEdited(true);
            setDefaultBranch(e.target.value);
            bubbleUnverified(gitUrl, credential, gitUsername, authMode, e.target.value);
          }}
        />
        <p className="field-help">
          The branch the factory treats as trunk. Leave blank for <code>main</code>.
        </p>
      </div>


      {showCredentialField && (
        <div className="field">
          <label className="field-label" htmlFor="git-cred">
            Credential · HTTPS access token
          </label>
          <PasswordInput
            id="git-cred"
            className="mono"
            toggleLabel="credential"
            placeholder={credentialPlaceholder(provider.kind)}
            value={credential}
            autoComplete="off-git-cred"
            onChange={(v) => {
              setCredential(v);
              bubbleUnverified(gitUrl, v, gitUsername, authMode, defaultBranch, false);
            }}
          />
        </div>
      )}

      {!hideAction && (
        <button
          type="button"
          className="btn"
          style={{ marginBottom: 16 }}
          onClick={() => void onTest()}
          disabled={!canTest}
        >
          {isLoading ? 'Testing…' : (testLabel ?? 'Test connection')}
        </button>
      )}

      {result && (
        <div className={`test-result ${result.outcome === 'success' ? 'success' : 'fail'}`} style={{ marginTop: 12 }}>
          <span className="ic">{result.outcome === 'success' ? '✓' : '!'}</span>
          <div>
            <div className="tr-title">
              {result.deferred ? 'saved' : result.outcome === 'success' ? 'success' : result.outcome}
            </div>
            <div className="tr-sub mono">
              {result.deferred
                ? 'Connection details saved — verified after the project is created.'
                : result.outcome === 'auth_failed'
                  ? authFailedMessage(provider.kind, authMode)
                  : OUTCOME_MESSAGE[result.outcome]}
              {result.head ? ` · HEAD ${result.head}` : ''}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="field-err" style={{ marginTop: 12 }} role="alert">
          {error}
        </div>
      )}

    </div>
  );
}
